import React from 'react';
import { useTranslation } from 'react-i18next';
import { SocialMedia } from '../../models/social-media/social-media';

interface SocialMediaSectionProps {
  socialMedia?: SocialMedia;
  className?: string;
}

const SocialMediaSection: React.FC<SocialMediaSectionProps> = ({ 
  socialMedia, 
  className = '' 
}) => {
  const { t } = useTranslation();

  // Don't render if no social media is provided
  if (!socialMedia || (!socialMedia.facebook && !socialMedia.instagram && !socialMedia.whatsapp && !socialMedia.youtube && !socialMedia.twitter)) {
    return null;
  }

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-6 ${className}`}>
      <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
        </svg>
        <span>{t('pages.ministry-details.socialMedia')}</span>
      </h3>
      
      <div className="space-y-3">
        {socialMedia.facebook && (
          <SocialMediaLink
            href={socialMedia.facebook}
            platform="Facebook"
            icon={<FacebookIcon />}
            className="bg-white border-2 border-gray-200 hover:border-black text-gray-700 hover:text-black"
          />
        )}
        
        {socialMedia.instagram && (
          <SocialMediaLink
            href={socialMedia.instagram}
            platform="Instagram"
            icon={<InstagramIcon />}
            className="bg-white border-2 border-gray-200 hover:border-black text-gray-700 hover:text-black"
          />
        )}
        
        {socialMedia.whatsapp && (
          <SocialMediaLink
            href={socialMedia.whatsapp}
            platform="WhatsApp"
            icon={<WhatsAppIcon />}
            className="bg-white border-2 border-gray-200 hover:border-black text-gray-700 hover:text-black"
          />
        )}
        
        {socialMedia.youtube && (
          <SocialMediaLink
            href={socialMedia.youtube}
            platform="YouTube"
            icon={<YouTubeIcon />}
            className="bg-white border-2 border-gray-200 hover:border-black text-gray-700 hover:text-black"
          />
        )}
        
        {socialMedia.twitter && (
          <SocialMediaLink
            href={socialMedia.twitter}
            platform="Twitter"
            icon={<TwitterIcon />}
            className="bg-white border-2 border-gray-200 hover:border-black text-gray-700 hover:text-black"
          />
        )}
      </div>
    </div>
  );
};

// Social Media Link Component
interface SocialMediaLinkProps {
  href: string;
  platform: string;
  icon: React.ReactNode;
  className: string;
}

const SocialMediaLink: React.FC<SocialMediaLinkProps> = ({ href, platform, icon, className }) => {
  const { t } = useTranslation();

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`w-full inline-flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200 font-medium text-sm group ${className}`}
    >
      <div className="flex items-center space-x-3">
        <div className="w-8 h-8 flex items-center justify-center">
          {icon}
        </div>
        <span>{t('general.social-media.follow-us-on', 'Follow us on')} {platform}</span>
      </div>
      <svg 
        className="w-4 h-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
      </svg>
    </a>
  );
};

// Social Media Icons
const FacebookIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="3" ry="3" />
    <circle cx="12" cy="12" r="3.2" fill="#fff" />
    <circle cx="17.5" cy="6.5" r="1" fill="#fff" />
  </svg>
);

const WhatsAppIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.085"/>
  </svg>
);

const YouTubeIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const TwitterIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

export default SocialMediaSection;